/**
 * Deduction engine for El Detective.
 *
 * A case is a small constraint problem: one culprit, N authored statements,
 * and a known number of false statements. We brute-force the culprit space and
 * only accept cases whose solution is UNIQUE — so every puzzle is doubt-free
 * solvable by pure reasoning, never by guessing.
 */
import { rngFrom, randInt, sample, shuffle, pick } from '../lib/random.js'
import { getCategory } from './themes.js'

export const DIFFICULTIES = [
  { id: 1, label: 'Calentamiento', suspects: 3, liars: 1, twoPer: false },
  { id: 2, label: 'Intermedio', suspects: 4, liars: 1, twoPer: false },
  { id: 3, label: 'Avanzado', suspects: 4, liars: 2, twoPer: true },
  { id: 4, label: 'Experto', suspects: 5, liars: 2, twoPer: false },
]

function truth(st, culpritId) {
  switch (st.type) {
    case 'accuse':
      return culpritId === st.target
    case 'clear':
      return culpritId !== st.target
    case 'deny':
      return culpritId !== st.speaker
    case 'confess':
      return culpritId === st.speaker
    case 'fact':
      return true
    default:
      return true
  }
}

/** All culprits consistent with the awaited number of lies. */
function candidates(suspects, statements, liars) {
  const out = []
  for (const c of suspects) {
    const falseCount = statements.filter((st) => !truth(st, c.id)).length
    if (falseCount === liars) out.push(c.id)
  }
  return out
}

function makeStatement(rng, speaker, suspects, used, cat) {
  const others = suspects.filter((s) => s.id !== speaker.id)
  const target = others.length ? pick(rng, others) : null
  const roll = rng()

  if (roll < 0.34 && target) return { type: 'accuse', speaker: speaker.id, target: target.id }
  if (roll < 0.55 && target) return { type: 'clear', speaker: speaker.id, target: target.id }
  if (roll < 0.82) return { type: 'deny', speaker: speaker.id, target: null }
  if (roll < 0.92) return { type: 'confess', speaker: speaker.id, target: null }
  return { type: 'fact', speaker: speaker.id, target: null, factIndex: randInt(rng, 0, cat.facts.length - 1) }
}

function buildStatements(rng, suspects, cat, diff) {
  const used = new Set()
  const statements = []
  const perSpeaker = {}

  for (const s of suspects) {
    const n = diff.twoPer ? 2 : 1
    for (let i = 0; i < n; i++) {
      let st = null
      for (let tries = 0; tries < 12; tries++) {
        const cand = makeStatement(rng, s, suspects, used, cat)
        const key = `${cand.speaker}:${cand.type}:${cand.target ?? ''}:${cand.factIndex ?? ''}`
        // avoid exact duplicates and self-contradictions (confess + deny)
        const contradicts =
          (cand.type === 'confess' && (perSpeaker[s.id] || []).includes('deny')) ||
          (cand.type === 'deny' && (perSpeaker[s.id] || []).includes('confess'))
        if (!used.has(key) && !contradicts) {
          st = cand
          used.add(key)
          break
        }
      }
      if (st) {
        statements.push(st)
        perSpeaker[s.id] = [...(perSpeaker[s.id] || []), st.type]
      }
    }
  }
  return statements
}

function fallbackCase(rng, cat, names) {
  const chosen = sample(rng, names, 3)
  const suspects = chosen.map((name, i) => ({ id: `s${i}`, name }))
  const statements = [
    { type: 'accuse', speaker: 's0', target: 's1' },
    { type: 'accuse', speaker: 's1', target: 's2' },
    { type: 'deny', speaker: 's2', target: null },
  ]
  return { suspects, statements, liars: 1 }
}

export function generateCase({ seed, categoryId, difficultyId }) {
  const rng = rngFrom(seed || Math.floor(Math.random() * 1e9))
  const cat = getCategory(categoryId)
  let diff = DIFFICULTIES.find((d) => d.id === difficultyId) || DIFFICULTIES[1]

  let core = null
  for (let attempt = 0; attempt < 3000 && !core; attempt++) {
    const suspects = sample(rng, cat.names, diff.suspects).map((name, i) => ({
      id: `s${i}`,
      name,
    }))
    const statements = buildStatements(rng, suspects, cat, diff)
    const cands = candidates(suspects, statements, diff.liars)
    if (cands.length === 1) core = { suspects, statements, liars: diff.liars }
  }
  if (!core) {
    core = fallbackCase(rng, cat, cat.names)
    diff = DIFFICULTIES[0]
  }

  const byId = Object.fromEntries(core.suspects.map((s) => [s.id, s]))
  const solutionId = candidates(core.suspects, core.statements, core.liars)[0]

  const rendered = core.statements.map((st) => ({
    ...st,
    text: renderStatement(st, byId, cat),
    truth: truth(st, solutionId),
  }))

  const bySuspect = core.suspects.map((s) => ({
    id: s.id,
    name: s.name,
    lines: rendered.filter((st) => st.speaker === s.id).map((st) => st.text),
  }))

  const liars = rendered.filter((st) => !st.truth)
  const culprit = byId[solutionId]

  const explanation =
    `El culpable es ${culprit.name}. ` +
    (core.liars === 1
      ? `Solo una declaración era falsa: ${liarLine(liars[0], byId)}. `
      : `Había ${core.liars} declaraciones falsas: ${liars.map((l) => liarLine(l, byId)).join(' ')} `) +
    'Si pruebas culpable a cada sospechoso, solo uno cuadra con el número exacto de mentiras.'

  return {
    seed,
    categoryId: cat.id,
    category: cat,
    difficulty: diff,
    intro: `En ${cat.place}, ${cat.object} desapareció. ${core.suspects.length} sospechosos declaran y sabes que exactamente ${core.liars === 1 ? 'una declaración es falsa' : `${core.liars} declaraciones son falsas`}.`,
    suspects: bySuspect,
    statements: rendered,
    solutionId,
    solutionName: culprit.name,
    liarIds: liars.map((l) => l.speaker),
    explanation,
  }
}

function renderStatement(st, byId, cat) {
  const sp = byId[st.speaker]
  const tg = st.target ? byId[st.target] : null
  switch (st.type) {
    case 'accuse':
      return `${tg.name} ${cat.verb} ${cat.object}.`
    case 'clear':
      return `${tg.name} es inocente, se lo aseguro.`
    case 'deny':
      return 'Yo no fui.'
    case 'confess':
      return 'Está bien... fui yo.'
    case 'fact':
      return cat.facts[st.factIndex ?? 0]
    default:
      return `${sp.name} no dice nada útil.`
  }
}

function liarLine(liar, byId) {
  if (!liar) return 'nadie'
  const sp = byId[liar.speaker]
  return `${sp ? sp.name : 'Alguien'} dijo «${liar.text}»`
}
